import React, { useCallback, useState, useRef, useEffect } from "react";
import {
  useRegisterSingleActiveMedia,
  useSingleActiveMedia,
} from "../../hooks/useSingleActiveMedia";

const FullMediaPreview = ({ isOpen, onClose, media, initialIndex = 0 }) => {
  const { register, claimActive, releaseActive } = useSingleActiveMedia();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const videoRef = useRef(null);

  const stopPlayback = useCallback(() => {
    if (videoRef.current && !videoRef.current.paused) {
      videoRef.current.pause();
    }
    setIsPlaying(false);
    setShowControls(true);
  }, []);

  useRegisterSingleActiveMedia(stopPlayback, register);

  const handleVideoPlay = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const handleVideoPause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const handleVideoLoadedMetadata = useCallback((e) => {
    const video = e.currentTarget;
    if (Number.isFinite(video.duration)) {
      setDuration(video.duration);
    }
  }, []);

  const handleVideoTimeUpdate = useCallback((e) => {
    const video = e.currentTarget;
    setCurrentTime(video.currentTime);
    if (Number.isFinite(video.duration) && video.duration > 0) {
      setProgress((video.currentTime / video.duration) * 100);
    }
  }, []);

  const handleVideoEnded = useCallback(() => {
    setIsPlaying(false);
    setShowControls(true);
    releaseActive();
  }, [releaseActive]);

  // Reset state when media changes
  useEffect(() => {
    if (media[currentIndex]?.type === "video") {
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime(0);
      setDuration(0);
      setShowControls(true);
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.pause();
      }
    }
  }, [currentIndex, media]);

  // Handle ESC key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  // Hide controls while playing after a short delay
  useEffect(() => {
    if (!isPlaying) return undefined;

    const timer = setTimeout(() => setShowControls(false), 3000);
    return () => clearTimeout(timer);
  }, [isPlaying, currentIndex]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || media[currentIndex]?.type !== "video") return;
    video.playbackRate = playbackRate;
  }, [playbackRate, currentIndex, media]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      claimActive(video);
      video.play().catch((error) => {
        console.error("Video play failed:", error);
        setIsPlaying(false);
        releaseActive();
      });
    } else {
      video.pause();
      releaseActive();
    }
    setShowControls(true);
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  const handleSeek = (e) => {
    if (!videoRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    videoRef.current.currentTime = pos * videoRef.current.duration;
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  const nextMedia = () => {
    if (currentIndex < media.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const prevMedia = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  if (!isOpen || !media || media.length === 0) return null;

  const currentMedia = media[currentIndex];

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10000,
        backgroundColor: "rgba(0, 0, 0, 0.95)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: currentMedia?.type === "image" ? "pointer" : "default",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && currentMedia?.type === "image") {
          onClose();
        }
      }}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        style={{
          position: "absolute",
          top: "20px",
          right: "20px",
          zIndex: 10001,
          background: "rgba(0, 0, 0, 0.7)",
          border: "none",
          borderRadius: "50%",
          width: "40px",
          height: "40px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          color: "white",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "rgba(0, 0, 0, 0.9)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "rgba(0, 0, 0, 0.7)";
        }}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>

      {/* Media display */}
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        onMouseEnter={() => setShowControls(true)}
        onMouseLeave={() => {
          if (isPlaying) {
            setTimeout(() => setShowControls(false), 2000);
          }
        }}
      >
        {currentMedia?.type === "image" ? (
          <img
            src={currentMedia.url}
            alt="Full preview"
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              width: "auto",
              height: "auto",
              objectFit: "contain",
            }}
            onClick={onClose}
          />
        ) : currentMedia?.type === "video" ? (
          <div
            style={{
              position: "relative",
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <video
              ref={videoRef}
              src={currentMedia.url}
              onClick={togglePlay}
              onPlay={handleVideoPlay}
              onPause={handleVideoPause}
              onTimeUpdate={handleVideoTimeUpdate}
              onLoadedMetadata={handleVideoLoadedMetadata}
              onDurationChange={handleVideoLoadedMetadata}
              onEnded={handleVideoEnded}
              muted={isMuted}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                width: "auto",
                height: "auto",
                objectFit: "contain",
                cursor: "pointer",
              }}
              playsInline
            >
              Your browser does not support the video tag.
            </video>

            {/* Video controls */}
            {showControls && (
              <div
                style={{
                  position: "absolute",
                  bottom: "20px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "90%",
                  maxWidth: "800px",
                  padding: "15px 20px",
                  backgroundColor: "rgba(0, 0, 0, 0.8)",
                  borderRadius: "12px",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "15px",
                    marginBottom: "10px",
                  }}
                >
                  <button
                    onClick={togglePlay}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "white",
                      padding: "8px",
                    }}
                  >
                    {isPlaying ? (
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="white"
                        strokeWidth="2"
                      >
                        <rect x="6" y="4" width="4" height="16" />
                        <rect x="14" y="4" width="4" height="16" />
                      </svg>
                    ) : (
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="white"
                        strokeWidth="2"
                      >
                        <polygon points="5 3 19 12 5 21 5 3" />
                      </svg>
                    )}
                  </button>

                  <button
                    onClick={toggleMute}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "white",
                      padding: "8px",
                    }}
                  >
                    {isMuted ? (
                      <img
                        src="/icons/mute.svg"
                        alt="Unmute"
                        style={{ width: "24px", height: "24px" }}
                      />
                    ) : (
                      <img
                        src="/icons/unmute.svg"
                        alt="Mute"
                        style={{ width: "24px", height: "24px" }}
                      />
                    )}
                  </button>

                  <div style={{ position: "relative" }}>
                    <button
                      onClick={() => setShowSpeedMenu((prev) => !prev)}
                      style={{
                        background: "none",
                        border: "1px solid rgba(255,255,255,0.5)",
                        borderRadius: "12px",
                        cursor: "pointer",
                        color: "white",
                        padding: "6px 12px",
                        fontSize: "14px",
                        minWidth: "50px",
                      }}
                    >
                      {playbackRate}x
                    </button>

                    {showSpeedMenu && (
                      <div
                        style={{
                          position: "absolute",
                          bottom: "140%",
                          left: 0,
                          backgroundColor: "rgba(0,0,0,0.9)",
                          borderRadius: "8px",
                          padding: "4px 0",
                          minWidth: "80px",
                        }}
                      >
                        {[0.5, 1, 1.25, 1.5, 2].map((rate) => (
                          <div
                            key={rate}
                            onClick={() => {
                              setPlaybackRate(rate);
                              setShowSpeedMenu(false);
                            }}
                            style={{
                              padding: "6px 12px",
                              fontSize: "14px",
                              color:
                                rate === playbackRate ? "#008CFF" : "#FFFFFF",
                              cursor: "pointer",
                              backgroundColor:
                                rate === playbackRate
                                  ? "rgba(0,140,255,0.2)"
                                  : "transparent",
                            }}
                          >
                            {rate}x
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <span
                    style={{
                      color: "white",
                      fontSize: "14px",
                      minWidth: "50px",
                    }}
                  >
                    {formatTime(currentTime)}
                  </span>
                  <div
                    style={{
                      flex: 1,
                      height: "6px",
                      backgroundColor: "rgba(255,255,255,0.3)",
                      borderRadius: "3px",
                      cursor: "pointer",
                    }}
                    onClick={handleSeek}
                  >
                    <div
                      style={{
                        width: `${progress}%`,
                        height: "100%",
                        backgroundColor: "#008CFF",
                        borderRadius: "3px",
                      }}
                    />
                  </div>
                  <span
                    style={{
                      color: "white",
                      fontSize: "14px",
                      minWidth: "50px",
                    }}
                  >
                    {formatTime(duration)}
                  </span>
                </div>
              </div>
            )}

            {/* Play button overlay */}
            {!isPlaying && (
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "80px",
                  height: "80px",
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
                onClick={togglePlay}
              >
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polygon points="5 3 19 12 5 21 5 3"></polygon>
                </svg>
              </div>
            )}
          </div>
        ) : null}

        {/* Navigation arrows for multiple media */}
        {media.length > 1 && (
          <>
            {currentIndex > 0 && (
              <button
                onClick={prevMedia}
                style={{
                  position: "absolute",
                  left: "20px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "rgba(0, 0, 0, 0.7)",
                  border: "none",
                  borderRadius: "50%",
                  width: "50px",
                  height: "50px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  zIndex: 10001,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(0, 0, 0, 0.9)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(0, 0, 0, 0.7)";
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
            )}

            {currentIndex < media.length - 1 && (
              <button
                onClick={nextMedia}
                style={{
                  position: "absolute",
                  right: "20px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "rgba(0, 0, 0, 0.7)",
                  border: "none",
                  borderRadius: "50%",
                  width: "50px",
                  height: "50px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  zIndex: 10001,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(0, 0, 0, 0.9)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(0, 0, 0, 0.7)";
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            )}

            {/* Media indicators */}
            <div
              style={{
                position: "absolute",
                bottom: "80px",
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                gap: "8px",
                zIndex: 10001,
              }}
            >
              {media.map((_, index) => (
                <div
                  key={index}
                  style={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    backgroundColor:
                      index === currentIndex
                        ? "#008CFF"
                        : "rgba(255,255,255,0.5)",
                    cursor: "pointer",
                  }}
                  onClick={() => setCurrentIndex(index)}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FullMediaPreview;
